import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RefreshCw, Upload } from "lucide-react";
import {
    useOrganization,
    useUser,
} from "@clerk/clerk-react";

import FileExplorer from "../sidebar/FileExplorer";
import { SidebarTab } from "../../../types";
import Spinner from "../ui/spinner";
import { cn } from "../../lib/utils";
import { filesystemService } from '../../utils/filesystem';
import { isBinaryFile } from "../../lib/fileUtils";
import { projectService } from "../../services/ProjectService";
import { pyodideService } from "../../services/PyodideService";
import { toast } from "sonner";

interface FileItem {
    path: string;
    name: string;
    type: "file" | "folder";
    size?: number;
    contents?: FileItem[];
};

interface SidebarProps {
    rootFolder: string | null;
    setSelectedFile: (filePath: string) => void;
    isCollapsed: boolean;
    activeTab: SidebarTab;
    setActiveTab: (tab: SidebarTab) => void;
    className?: string;
    appLoading: boolean;
    isPyodideLoaded?: boolean;
}

const Sidebar: FC<SidebarProps> = ({
    rootFolder,
    setSelectedFile,
    isCollapsed,
    activeTab,
    setActiveTab,
    className,
    appLoading,
    isPyodideLoaded = false,
}) => {
    const { user } = useUser();
    const { organization } = useOrganization();
    const [files, setFiles] = useState<FileItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [activeFilePath, setActiveFilePath] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (user) {
            projectService.setUserContext(user.id, organization?.id || null);
        }
    }, [user, organization]);

    useEffect(() => {
        if (rootFolder && !appLoading && isPyodideLoaded) {
            fetchFiles();
        } else {
            setFiles([]);
        }
    }, [rootFolder, appLoading, isPyodideLoaded]);

    useEffect(() => {
        const checkPyodideStatus = () => {
            // Just check if files need to be refreshed
            if (rootFolder && !appLoading && isPyodideLoaded) {
                fetchFiles();
            }
        };

        const handleFilesystemChange = (event: CustomEvent<{ path: string; source?: string }>) => {
            console.log('[Sidebar] filesystem:file-changed event:', event.detail);
            if (event.detail.source !== 'editor-save') {
                console.log('[Sidebar] Non-editor change detected, refreshing files');
                checkPyodideStatus();
            } else {
                console.log('[Sidebar] Editor save detected, ignoring file change');
            }
        };

        const unsubscribe = pyodideService.subscribeToWorkerMessage('FILES_LOADED', () => {
            checkPyodideStatus();
        });

        const readyUnsubscribe = pyodideService.subscribeToWorkerMessage('READY', () => {
            checkPyodideStatus();
        });

        const fileChangeUnsubscribe = pyodideService.subscribeToWorkerMessage('FILES_CHANGED', () => {
            checkPyodideStatus();
        });

        window.addEventListener('filesystem:file-changed', handleFilesystemChange as EventListener);

        return () => {
            unsubscribe();
            readyUnsubscribe();
            fileChangeUnsubscribe();
            window.removeEventListener('filesystem:file-changed', handleFilesystemChange as EventListener);
        };
    }, [rootFolder, appLoading, isPyodideLoaded]);

    const fetchFiles = async () => {
        if (loading || !isPyodideLoaded) {
            console.log('[Sidebar] fetchFiles called but skipping - loading:', loading, 'isPyodideLoaded:', isPyodideLoaded);
            return;
        }

        setLoading(true);
        try {
            const filesList = await filesystemService.listPyodideFiles('/project');
            const currentFilesStr = JSON.stringify(files);
            const newFilesStr = JSON.stringify(filesList);

            if (currentFilesStr !== newFilesStr) {
                console.log('[Sidebar] File structure changed, updating files state');
                setFiles(filesList);
            } else {
                console.log('[Sidebar] File structure unchanged, keeping current files state');
            }
        } catch (err) {
            console.error('Error fetching files:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleItemClick = useCallback((path: string, type: "file" | "folder") => {
        if (type === "file") {
            if (path !== activeFilePath) {
                setActiveFilePath(path);
                setSelectedFile(path);
                setActiveTab(SidebarTab.Editor);
            }
        }
    }, [activeFilePath, setActiveFilePath, setSelectedFile, setActiveTab, SidebarTab.Editor]);

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement> | DragEvent) => {
        let selectedFiles: FileList | null = null;

        // Handle both input change events and drag events
        if (event instanceof DragEvent) {
            selectedFiles = event.dataTransfer?.files || null;
            console.log("Drag event detected, files:", selectedFiles?.length);
        } else {
            selectedFiles = event.target.files;
            console.log("Input change event detected, files:", selectedFiles?.length);
        }

        if (!selectedFiles || selectedFiles.length === 0) {
            console.log("No files selected");
            return;
        }

        try {
            // Ensure data directory exists
            await filesystemService.createDirectory('/project/data');
            // Ensure images directory exists
            await filesystemService.createDirectory('/project/images');

            // Create a batch of files to upload
            const filesToUpload: Record<string, string | ArrayBuffer> = {};
            const FILE_SIZE_LIMIT = 10 * 1024 * 1024; // 10MB in bytes

            for (let i = 0; i < selectedFiles.length; i++) {
                const file = selectedFiles[i];

                if (file.size > FILE_SIZE_LIMIT) {
                    toast.error(
                        `File ${file.name} exceeds the 10MB limit. For larger datasets, please contact sales.`,
                        {
                            description: "Schedule a call: cal.com/amruthagujjar",
                            action: {
                                label: "Schedule",
                                onClick: () => window.open("https://cal.com/amruthagujjar", "_blank")
                            },
                            duration: 6000,
                        }
                    );
                    continue;
                }

                // Check if file is an image
                const isImage = file.type.startsWith('image/') ||
                    /\.(jpe?g|png|gif|bmp|webp|svg)$/i.test(file.name);

                const isCsv = file.type === 'text/csv' ||
                    file.name.toLowerCase().endsWith('.csv');

                if (!isImage && !isCsv) {
                    toast.error(
                        `Only CSV and image files are allowed`,
                        {
                            duration: 4000,
                        }
                    );
                    continue;
                }

                // Determine directory based on file type
                const targetDir = isImage ? '/project/images/' : '/project/data/';
                const filePath = `${targetDir}${file.name}`;

                try {
                    if (isBinaryFile(file.name)) {
                        // Handle binary files
                        const buffer = await file.arrayBuffer();
                        filesToUpload[filePath] = buffer;
                    } else {
                        // Handle text files
                        const content = await file.text();
                        filesToUpload[filePath] = content;
                    }
                } catch (error) {
                    console.error(`Error preparing file ${file.name}:`, error);
                }
            }

            // Upload all files at once
            if (Object.keys(filesToUpload).length > 0) {
                if (pyodideService.isReady()) {
                    await pyodideService.loadFiles(filesToUpload);
                } else {
                    for (const [path, content] of Object.entries(filesToUpload)) {
                        filesystemService.pendingFiles[path] = content;
                    }
                }
            }

            // Refresh file list
            fetchFiles();

        } catch (error) {
            console.error('Error during file upload:', error);
        } finally {
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const memoizedFileExplorer = useMemo(() => {
        if (loading) {
            return (
                <div className="flex items-center justify-center h-full">
                    <Spinner size="md" />
                </div>
            );
        }

        return (
            <>
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    multiple
                    className="hidden"
                />
                <FileExplorer
                    files={files}
                    isCollapsed={isCollapsed}
                    onItemClick={handleItemClick}
                    onFilesUpdated={fetchFiles}
                    activeFilePath={activeFilePath as string | undefined}
                    onUploadFiles={(event) => {
                        if (event) {
                            handleFileUpload(event);
                        } else {
                            fileInputRef.current?.click();
                        }
                    }}
                />
            </>
        );
    }, [loading, files, isCollapsed, handleItemClick, fetchFiles, activeFilePath, fileInputRef, handleFileUpload]);

    return (
        <div
            className={cn(
                "flex flex-col h-full bg-white border-r border-gray-200 group",
                isCollapsed ? "w-[52px]" : "w-[240px]",
                className
            )}
        >
            {/* Sidebar Header */}
            <div className="flex items-center justify-between h-10 px-4 shrink-0">
                <div className="flex items-center gap-2">
                    {!isCollapsed && (
                        <span className="text-sm font-medium text-gray-700 truncate">
                            {rootFolder || 'No folder selected'}
                        </span>
                    )}
                </div>
                {!isCollapsed && (
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-gray-200"
                            title="Upload files"
                        >
                            <Upload className="h-4 w-4 text-gray-500" />
                        </button>
                        <button
                            onClick={() => fetchFiles()}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-gray-200"
                            title="Refresh files"
                        >
                            <RefreshCw className="h-4 w-4 text-gray-500" />
                        </button>
                    </div>
                )}
            </div>

            <div className="flex-1 overflow-auto flex flex-col">
                {rootFolder ? (
                    <div className="flex-1 overflow-auto min-h-0">
                        {memoizedFileExplorer}
                    </div>
                ) : (
                    <div className="flex-1" />
                )}
            </div>
        </div>
    );
};

export default Sidebar;
